/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import MuiButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer } from '@mui/material';
import { ButtonClose, TitleProps } from './types';

type OmitProps = 'onSubmit' | 'sx' | 'title';
interface Props extends Omit<DialogProps, OmitProps> {
  open: boolean;
  title: string;
  serverError?: boolean;
  positive?: string;
  negative?: string;
  disabled?: boolean;
  positiveDisabled?: boolean;
  negativeDisabled?: boolean;
  onPositive?: () => void | Promise<void>;
  onNegative: (e: null | React.SyntheticEvent) => void;
  onSubmit?: (event: React.SyntheticEvent<HTMLFormElement>) => void;
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(4),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
  '& .MuiDrawer-paper': {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 600,
    },
    boxSizing: 'border-box',
  },
  '& .MuiCollapse-wrapper': {
    marginBottom: theme.spacing(1),
  },
  '& .MuiAccordionDetails-root': {
    padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
  },
  '& .MuiTabs-root': {
    paddingBottom: theme.spacing(1.5),
  },
  '& .MuiTab-root': {
    width: '50%',
  },
}));

const StyledDrawerPositiveButton = styled(MuiButton)(({ theme }) => ({
  color: '#4BAEF8',
  fontWeight: 700,
  '&:hover': {
    backgroundColor: 'rgba(75, 174, 248, 0.08)',
    color: theme.palette.secondary.main,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
}));

const StyledDrawerNegativeButton = styled(MuiButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 700,
  '&:hover': {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
}));

const StyledDrawerContent = styled(MuiDialogContent)({
  display: 'flex',
  flexDirection: 'column',
});

const StyledDrawerTitle = (props: TitleProps) => {
  const { children, onClose } = props;

  return (
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', paddingRight: '15px' }}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose as ButtonClose}
          sx={{
            padding: '0px 4px',
            color: '#000',
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

/**
 * A simple 'Cancel'/'Submit' dialog that complies with our design
 * standards and allows for defined customizations
 */
export default function CustomDrawer({
  open,
  onNegative,
  onPositive,
  disabled,
  positiveDisabled,
  negativeDisabled,
  serverError,
  onSubmit,
  title,
  ...props
}: Props) {
  return (
    <StyledDrawer
      data-testid="drawer"
      {...props}
      anchor="right"
      onClose={props.onClose}
      open={open}
    >
      <StyledDrawerTitle data-testid="dialog-title" onClose={props.onClose}>
        {title}
      </StyledDrawerTitle>
      <StyledDrawerContent dividers>
        <form noValidate onSubmit={onSubmit} id="dialog-form">
          {props.children}
        </form>
      </StyledDrawerContent>
      <DialogActions>
        <StyledDrawerNegativeButton
          disabled={negativeDisabled || disabled}
          onClick={onNegative}
        >
          {props.negative ?? 'Cancel'}
        </StyledDrawerNegativeButton>
        <StyledDrawerPositiveButton
          form={onSubmit ? 'dialog-form' : undefined}
          type={onSubmit ? 'submit' : 'button'}
          disabled={positiveDisabled || disabled}
          onClick={onPositive}
          data-testid="test-positive-button"
        >
          {serverError ? 'Resubmit' : props.positive ?? 'Submit'}
        </StyledDrawerPositiveButton>
      </DialogActions>
    </StyledDrawer>
  );
}
